import MapboxLocationSearch from '@apps-common/location-search/MapboxLocationSearch.jsx';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  SOURCE_TYPES,
  suggestSavedSearches,
  suggestSearchItemIds,
} from './util.js';
import {notify} from '@explorer/actions/notifications.js';
import {
  clearSearch,
  executeSearch,
  loadSearch,
  searchSingleItem,
} from '@explorer/actions/search';
import {trackEvent} from '@explorer/analytics';
import {useGetSavedSearchesQuery} from '@explorer/api/apiSlice.js';
import {MAPBOX_ACCESS_TOKEN} from '@explorer/config.js';
import {getAvailableItemTypes} from '@explorer/selectors/item-types';
import {hydrateSearchGeometry} from '@explorer/util/granero-resolver';

const ConnectedSearchInput = () => {
  const dispatch = useDispatch();

  const [inputValue, setInputValue] = useState('');

  const center = useSelector(state => state.map.center);
  const itemTypes = useSelector(getAvailableItemTypes)?.types;
  const searchName = useSelector(state => state.search.name);

  const {data: savedSearches} = useGetSavedSearchesQuery(undefined);

  useEffect(() => {
    setInputValue(searchName);
  }, [searchName]);

  const onSelect = async result => {
    if (result) {
      trackEvent('Search Result selected', {
        query: result.label || result.title,
        isSavedSearch: !!result.savedSearch,
        source: result.source,
        featureClass: result.suggestion?.__type,
      });

      if (result.source === SOURCE_TYPES.SAVED_SEARCHES) {
        const hydratedSearch = await hydrateSearchGeometry(result.savedSearch);
        dispatch(loadSearch(hydratedSearch));
      } else if (result.source === SOURCE_TYPES.ITEM_IDS) {
        dispatch(searchSingleItem(result.searchItem));
      } else {
        let showCenterMarker = [
          SOURCE_TYPES.MGRS_POINT,
          SOURCE_TYPES.LATLON,
        ].includes(result.source);
        if (result.source === SOURCE_TYPES.MAPBOX) {
          showCenterMarker = ['address', 'street'].includes(
            result.suggestion.properties.feature_type
          );
        }

        dispatch(executeSearch({...result, showCenterMarker}));
      }
    }
  };

  const onSuggestSavedSearches = query =>
    suggestSavedSearches(query, savedSearches);
  const onSuggestSearchItemIds = query =>
    suggestSearchItemIds(query, itemTypes);

  const handleOnClearInput = () => dispatch(clearSearch());

  const onError = () => {
    dispatch(
      notify({
        message: 'There was an error executing the search',
        severity: 'error',
        persist: true,
      })
    );
  };

  return (
    <MapboxLocationSearch
      center={[center[1], center[0]]}
      onSelectOption={onSelect}
      onClear={handleOnClearInput}
      suggestionsPromises={[onSuggestSavedSearches, onSuggestSearchItemIds]}
      inputValue={inputValue}
      onError={onError}
      accessToken={MAPBOX_ACCESS_TOKEN}
    />
  );
};

export default ConnectedSearchInput;
